import { useEffect, useState } from 'react';
import { getAdminEnrollments, adminEnrollEmployee, GetAdminEnrollmentsOutputType, getAdminEmployees, GetAdminEmployeesOutputType } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Plus, Users, Search, CheckCircle, Clock, AlertTriangle, BookOpen } from 'lucide-react';

type Enrollment = GetAdminEnrollmentsOutputType['enrollments'][0];
type Course = GetAdminEnrollmentsOutputType['courses'][0];

const STATUS_COLORS: Record<string, string> = {
  'Assigned': 'bg-muted text-muted-foreground',
  'Learning': 'bg-primary/15 text-primary',
  'Quiz Available': 'bg-accent/20 text-accent-foreground',
  'Quiz Passed': 'bg-primary/20 text-primary',
  'Assessment Requested': 'bg-orange-500/15 text-orange-600',
  'Assessment In Progress': 'bg-orange-500/15 text-orange-600',
  'Approved': 'bg-primary/20 text-primary',
  'Course Complete': 'bg-primary/20 text-primary',
  'Further Training': 'bg-destructive/15 text-destructive',
  'Paused': 'bg-muted text-muted-foreground',
};

function EnrollmentRow({ e }: { e: Enrollment }) {
  const cls = STATUS_COLORS[e.status] || 'bg-muted text-muted-foreground';
  return (
    <div className="flex items-center gap-3 p-3 bg-card border border-border rounded-xl">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{e.employeeName || e.employeeEmail}</p>
        <p className="text-xs text-muted-foreground truncate">{e.courseTitle} · {e.courseCode}</p>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${cls}`}>{e.status}</span>
          {e.assignedDate && <span className="text-[10px] text-muted-foreground">Assigned: {new Date(e.assignedDate).toLocaleDateString('en-GB')}</span>}
          {e.dueDate && <span className="text-[10px] text-destructive">Due: {new Date(e.dueDate).toLocaleDateString('en-GB')}</span>}
        </div>
      </div>
      <span className="text-xs text-muted-foreground font-mono shrink-0">{e.reference}</span>
    </div>
  );
}

export default function EnrollmentsSection() {
  const [data, setData] = useState<GetAdminEnrollmentsOutputType | null>(null);
  const [loading, setLoading] = useState(true);
  const [filterCourse, setFilterCourse] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [employees, setEmployees] = useState<GetAdminEmployeesOutputType['employees']>([]);
  const [selEmployee, setSelEmployee] = useState('');
  const [selCourse, setSelCourse] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [assigning, setAssigning] = useState(false);

  const load = () => {
    setLoading(true);
    getAdminEnrollments({
      courseId: filterCourse && filterCourse !== 'all' ? filterCourse : undefined,
      status: filterStatus && filterStatus !== 'all' ? filterStatus : undefined,
    })
      .then(setData)
      .catch(() => toast.error('Failed to load enrolments'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [filterCourse, filterStatus]);

  const handleAdd = async () => {
    if (!selEmployee || !selCourse) return;
    setAssigning(true);
    try {
      const result = await adminEnrollEmployee({ employeeId: selEmployee, courseId: selCourse, dueDate: dueDate || undefined });
      toast.success(result.message);
      setShowAdd(false);
      setSelEmployee('');
      setSelCourse('');
      setDueDate('');
      load();
    } catch (e: any) {
      toast.error(e.message || 'Failed to enrol');
    } finally {
      setAssigning(false);
    }
  };

  const openAddDialog = async () => {
    setShowAdd(true);
    if (employees.length === 0) {
      const res = await getAdminEmployees({}).catch(() => null);
      if (res) setEmployees(res.employees);
    }
  };

  const filtered = (data?.enrollments || []).filter(e => {
    if (!search) return true;
    const q = search.toLowerCase();
    return e.employeeName.toLowerCase().includes(q) || e.employeeEmail.toLowerCase().includes(q) || e.reference.toLowerCase().includes(q);
  });

  if (loading && !data) return (
    <div className="space-y-3">
      <Skeleton className="h-8 w-48" />
      {[1,2,3].map(i => <Skeleton key={i} className="h-20" />)}
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
          <Users className="w-5 h-5" /> Enrolments ({data?.enrollments.length || 0})
        </h2>
        <Button size="sm" onClick={openAddDialog}>
          <Plus className="w-4 h-4 mr-1" /> Assign Course
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-muted-foreground" />
          <Input placeholder="Search employee or reference…" value={search} onChange={e => setSearch(e.target.value)} className="pl-8 h-9 text-sm" />
        </div>
        <Select value={filterCourse} onValueChange={setFilterCourse}>
          <SelectTrigger className="w-[180px] h-9 text-sm"><SelectValue placeholder="All courses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All courses</SelectItem>
            {(data?.courses || []).map(c => (
              <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[160px] h-9 text-sm"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {['Assigned','Learning','Quiz Passed','Assessment Requested','Course Complete','Further Training'].map(s => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* List */}
      <div className="space-y-2">
        {filtered.length === 0 ? (
          <div className="py-12 text-center">
            <BookOpen className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No enrolments found</p>
          </div>
        ) : filtered.map(e => (
          <EnrollmentRow key={e.id} e={e} />
        ))}
      </div>

      {/* Add dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Assign Course Enrolment</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm">Employee</Label>
              <Select value={selEmployee} onValueChange={setSelEmployee}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select employee…" /></SelectTrigger>
                <SelectContent>
                  {employees.map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.firstName} {e.lastName} ({e.email})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Course</Label>
              <Select value={selCourse} onValueChange={setSelCourse}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select course…" /></SelectTrigger>
                <SelectContent>
                  {(data?.courses || []).map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.title} ({c.code})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Due Date (optional)</Label>
              <Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={handleAdd} disabled={assigning || !selEmployee || !selCourse}>
              {assigning ? 'Assigning…' : 'Assign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
